import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { buildTimelineScadenze, type VoceScadenza } from '../services/scadenzeUnificate';
import { formatCurrency, formatDate } from '../services/cashFlowCalculations';
import { useCreateScadenza, useUpdateScadenza, useDeleteScadenza } from '../hooks/useScadenze';
import type { Finanziamento, Scadenza } from '../types';

const CATEGORIE = ['mutuo', 'assicurazione', 'manutenzione', 'burocrazia', 'altro'] as const;

export function Scadenze() {
  const { currentProject } = useProjectContext();
  const createScadenza = useCreateScadenza();
  const updateScadenza = useUpdateScadenza();
  const deleteScadenza = useDeleteScadenza();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Scadenza | null>(null);
  const [titolo, setTitolo] = useState('');
  const [data, setData] = useState('');
  const [categoria, setCategoria] = useState<string>('altro');
  const [note, setNote] = useState('');
  const [errore, setErrore] = useState<string | null>(null);

  const timeline = useMemo(() => {
    const finanziamenti: Finanziamento[] = currentProject?.finanziamenti ?? [];
    const scadenze: Scadenza[] = currentProject?.scadenze ?? [];
    return buildTimelineScadenze(finanziamenti, scadenze);
  }, [currentProject]);

  const completate = useMemo<Scadenza[]>(() => {
    const scadenze: Scadenza[] = currentProject?.scadenze ?? [];
    return scadenze.filter((s) => s.completata);
  }, [currentProject]);

  function resetForm() {
    setEditing(null);
    setTitolo('');
    setData('');
    setCategoria('altro');
    setNote('');
    setErrore(null);
    setShowForm(false);
  }

  function apriModifica(s: Scadenza) {
    setEditing(s);
    setTitolo(s.titolo);
    setData(s.data.slice(0, 10));
    setCategoria(s.categoria ?? 'altro');
    setNote(s.note ?? '');
    setErrore(null);
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrore(null);

    if (!titolo.trim() || !data) {
      setErrore('Titolo e data sono obbligatori');
      return;
    }
    if (!currentProject) return;

    try {
      if (editing) {
        await updateScadenza.mutateAsync({
          id: editing.id,
          projectId: currentProject.id,
          data: { titolo: titolo.trim(), data, categoria, note: note.trim() || null },
        });
      } else {
        await createScadenza.mutateAsync({
          projectId: currentProject.id,
          titolo: titolo.trim(),
          data,
          categoria,
          note: note.trim() || null,
        });
      }
      resetForm();
    } catch {
      setErrore('Salvataggio non riuscito. Riprova.');
    }
  }

  async function toggleCompletata(s: Scadenza) {
    if (!currentProject) return;
    await updateScadenza.mutateAsync({
      id: s.id,
      projectId: currentProject.id,
      data: { completata: !s.completata },
    });
  }

  async function handleDelete(s: Scadenza) {
    if (!currentProject) return;
    if (!window.confirm(`Eliminare la scadenza "${s.titolo}"?`)) return;
    await deleteScadenza.mutateAsync({ id: s.id, projectId: currentProject.id });
  }

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per visualizzare le scadenze</p>
      </div>
    );
  }

  const totaleRateResidue = timeline.voci.reduce(
    (sum, v) => sum + (v.kind === 'rata' ? v.importo : 0),
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Scadenze</h2>
          <p className="text-gray-600 mt-1">Rate dei finanziamenti e promemoria personali</p>
        </div>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className="btn-primary"
        >
          {showForm ? 'Annulla' : '+ Nuova scadenza'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">
            {editing ? 'Modifica scadenza' : 'Nuova scadenza'}
          </h3>

          {errore && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{errore}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="input-group">
              <label className="input-label" htmlFor="scad-titolo">
                Titolo
              </label>
              <input
                id="scad-titolo"
                type="text"
                value={titolo}
                onChange={(e) => setTitolo(e.target.value)}
                placeholder="Es. Comunicare scelta tasso mutuo"
                className="w-full"
                maxLength={200}
              />
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="scad-data">
                Data
              </label>
              <input
                id="scad-data"
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="scad-categoria">
                Categoria
              </label>
              <select
                id="scad-categoria"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                className="w-full"
              >
                {CATEGORIE.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group md:col-span-2">
              <label className="input-label" htmlFor="scad-note">
                Note (facoltative)
              </label>
              <textarea
                id="scad-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full"
                maxLength={2000}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={createScadenza.isPending || updateScadenza.isPending}
            >
              {createScadenza.isPending || updateScadenza.isPending ? 'Salvataggio…' : 'Salva'}
            </button>
            <button type="button" onClick={resetForm} className="btn-secondary">
              Annulla
            </button>
          </div>
        </form>
      )}

      {/* Scadute */}
      {timeline.scadute.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
          <div className="px-6 py-3 border-b border-red-200">
            <h3 className="font-semibold text-red-900">Scadute ({timeline.scadute.length})</h3>
          </div>
          <div className="divide-y divide-red-100">
            {timeline.scadute.map((v) => (
              <VoceRow
                key={v.id}
                voce={v}
                onToggle={toggleCompletata}
                onEdit={apriModifica}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {timeline.voci.length === 0 && timeline.scadute.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="text-5xl mb-3">🔔</div>
          <h3 className="text-lg font-medium text-gray-900">Nessuna scadenza</h3>
          <p className="mt-2 text-sm text-gray-500">
            Aggiungi una scadenza con il pulsante qui sopra, oppure inserisci un mutuo o un
            prestito con data prima rata.
          </p>
          <Link
            to="/finanziamento"
            className="inline-block mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            Vai ai Finanziamenti
          </Link>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {timeline.prossima && (
              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-primary-500">
                <p className="text-sm text-gray-500">Prossima scadenza</p>
                <p
                  className="text-2xl font-bold text-gray-900 mt-1"
                  data-testid="prossima-scadenza"
                >
                  {timeline.prossima.kind === 'rata'
                    ? formatCurrency(timeline.prossima.importo)
                    : timeline.prossima.titolo}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(timeline.prossima.date)}
                  {timeline.prossima.kind === 'rata' && ` · ${timeline.prossima.titolo}`}
                </p>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Entro 30 giorni</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="imminenti">
                {timeline.imminenti.length}
              </p>
              <p className="text-sm text-gray-600 mt-1">scadenze imminenti</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Totale rate residue</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(totaleRateResidue)}
              </p>
              <p className="text-sm text-gray-600 mt-1">somma delle rate future</p>
            </div>
          </div>

          {/* Timeline unificata */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Prossime scadenze</h3>
            </div>
            <div className="max-h-[32rem] overflow-y-auto divide-y divide-gray-100">
              {timeline.voci.map((v, idx) => (
                <VoceRow
                  key={v.id}
                  voce={v}
                  evidenzia={idx === 0}
                  onToggle={toggleCompletata}
                  onEdit={apriModifica}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Completate */}
      {completate.length > 0 && (
        <details className="bg-white rounded-lg shadow overflow-hidden">
          <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-700">
            Completate ({completate.length})
          </summary>
          <div className="divide-y divide-gray-100 border-t border-gray-200">
            {completate.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggleCompletata(s)}
                    aria-label={`Riapri ${s.titolo}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500 line-through truncate">
                      {s.titolo}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(s.data)}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(s)}
                  className="text-sm text-red-600 hover:text-red-700 shrink-0"
                >
                  Elimina
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

interface VoceRowProps {
  voce: VoceScadenza;
  evidenzia?: boolean;
  onToggle: (s: Scadenza) => void;
  onEdit: (s: Scadenza) => void;
  onDelete: (s: Scadenza) => void;
}

function VoceRow({ voce, evidenzia, onToggle, onEdit, onDelete }: VoceRowProps) {
  const giorno = new Date(`${voce.date}T00:00:00.000Z`).getUTCDate();

  if (voce.kind === 'rata') {
    return (
      <div
        className={`flex items-center justify-between px-6 py-3 ${evidenzia ? 'bg-primary-50' : ''}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-sm font-semibold shrink-0">
            {giorno}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{formatDate(voce.date)}</p>
            <p className="text-xs text-gray-500 truncate">
              {voce.titolo} · rata {voce.rata.numero}/{voce.rata.totaleRate}
            </p>
          </div>
        </div>
        <p className="font-semibold text-gray-900 currency shrink-0">
          {formatCurrency(voce.importo)}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-between px-6 py-3 gap-3 ${evidenzia ? 'bg-amber-50' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <input
          type="checkbox"
          checked={voce.completata}
          onChange={() => onToggle(voce.scadenza)}
          aria-label={`Segna come completata: ${voce.titolo}`}
          className="shrink-0"
        />
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-sm font-semibold shrink-0">
          {giorno}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{voce.titolo}</p>
          <p className="text-xs text-gray-500 truncate">
            {formatDate(voce.date)}
            {voce.categoria ? ` · ${voce.categoria}` : ''}
            {voce.note ? ` · ${voce.note}` : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={() => onEdit(voce.scadenza)}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Modifica
        </button>
        <button
          onClick={() => onDelete(voce.scadenza)}
          className="text-sm text-red-600 hover:text-red-700"
        >
          Elimina
        </button>
      </div>
    </div>
  );
}
